import {ApiBase} from "./ApiBase";
import {AxiosResponse} from "axios";
import {
    IBenchmarkReturn,
    IPlanTreeNode,
    IPlanTreeQuarterlyPerformance,
    IPlanTreeTargetAllocation, ITreeNodeBase
} from "../model/Portfolio.model";
import {IRangeValue} from "./InFormReportingApi";

export enum PARIS_PORTFOLIO_PLAN_TYPE {
    CORPORATE_DB = "Corporate DB",
    PUBLIC_DB = "Public DB",
    ENDOWMENT_FOUNDATIONS = "Endowment/Foundations"
}

export enum PEER_UNIVERSE_MEDIAN {
    ABOVE_MEDIAN = "Above Median",
    BELOW_MEDIAN = "Below Median",
    AT_MEDIAN = "At Median",
    UNKNOWN = "Unknown"
}

export interface IPlanCashFlow {
    date: Date;
    netCashFlow: number;
}

export interface IPlanAssetTrend {
    date: Date;
    marketValue: number;
    totalInflow: number | null;
    totalOutflow: number | null;
}
export interface IAssetAllocation {
    assetClassId: string;
    assetClass: string;
    marketValuePctOfTotal: number | null;
}

export interface IBuildingBlock {
    name: string;
    assetAllocations: IAssetAllocation[];
    totalPercentage: number | null;
}

export interface ILiquidityProfile {
    name: string;
    buildingBlocks: IBuildingBlock[];
    totalPercentage: number | null;
}
export interface IPortfolio {
    id: number;
    name: string;
    liquidityProfiles: ILiquidityProfile[];
}

export interface IPlanRelativePerformance {
    date: Date;
    relativeReturn: number;
}
 export interface IAssetAllocationComparison extends IAssetAllocation{
     modelMarketValuePctOfTotal: number | null;
 }

 export interface IBuildingBlockComparison extends IBuildingBlock {
    assetAllocations: IAssetAllocationComparison[];
    modelTotalPercentage: number | null;
 }

 export interface ILiquidityProfileComparison extends ILiquidityProfile {
    buildingBlocks: IBuildingBlockComparison[];
    modelTotalPercentage: number | null;
 }

 export interface IModelPortfolioComparison extends IPortfolio {
    liquidityProfiles: ILiquidityProfileComparison[];
 }

 export interface IPortfolioComparison {
    planType: string;
    asOfDate: string;
    currentPlanPortfolio: IPortfolio;
    modelPortfolios: IPortfolio[];
 }

    export interface IPortfolioMetrics {
        portfolioId: number,
        portfolioMedianLogNormReturn?: number,
        portfolioLogNormVol?: number,
        portfolioSharpe?: number,
        fivePctVar1y?: number,
        fivePctUpside1y?: number,
    }

 export interface IPerformanceMetrics {
     currentPlanPortfolioMetrics?: IPortfolioMetrics;
     modelPortfolioMetrics?: IPortfolioMetrics[];
 }

 export interface IPortfolioFeesTreeNode extends ITreeNodeBase {
     peerUniverseName: string | null,
     netExpenseRatio: number | null,
     revenueShare: number | null,
     effectiveNetExpenseRatio: number | null,
     peerUniverseMedian: string  | null,
     percentile25: number | null,
     percentile50: number | null,
     percentile75: number | null,
     population: number | null,

 }

export class PortfolioApi {
    public static getCurrentPlanPortfolioPlanType(): Promise<IPortfolio> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/plan-type", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanPortfolioTree(): Promise<IPlanTreeNode[]> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/plan-tree", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanPortfolioTargetAllocations(): Promise<IPlanTreeTargetAllocation[]>
    {
        return ApiBase.processGetUnwrapped(`/plans/current/portfolio/target-allocations`,
            (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanMarketValues(): Promise<IRangeValue[]> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/market-values", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanCashFlow(): Promise<IPlanCashFlow[]> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/cash-flow", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getBenchmarkReturns(): Promise<IBenchmarkReturn[]> {
        return ApiBase.processGetUnwrapped("/portfolio/benchmark-return", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanAssetTrends(): Promise<IPlanAssetTrend[]> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/asset-trends", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanQuarterlyPerformance(): Promise<IPlanTreeQuarterlyPerformance>
    {
        return ApiBase.processGetUnwrapped(`/plans/current/portfolio/quarterly-performance`,
            (response: AxiosResponse) => {
                return response.data;
            });
    }

    public static getCurrentPlanRelativePerformances(): Promise<IPlanRelativePerformance[]>
    {
        return ApiBase.processGetUnwrapped(`/plans/current/portfolio/relative-performances`,
            (response: AxiosResponse) => {
                return response.data;
            });
    }

    public static getCurrentPlanPortfolioComparison(): Promise<IPortfolioComparison> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio-comparison", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getPerformanceMetrics(): Promise<IPerformanceMetrics> {
        return ApiBase.processGetUnwrapped("/plans/current/performance-metrics", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getCurrentPlanType(): Promise<string> {
        return ApiBase.processGetUnwrapped("/plans/current/plan-type", (response: AxiosResponse) => {
            return response.data;
        });
    }

    public static getPortfolioFees(): Promise<IPortfolioFeesTreeNode[]> {
        return ApiBase.processGetUnwrapped("/plans/current/portfolio/fees", (response: AxiosResponse) => {
            return response.data;
        });
    }
}